import { percent } from "@amcharts/amcharts5";
import { IPieChartSettings, PieChart, PieSeries } from "@amcharts/amcharts5/percent";
import { XYChart } from "@amcharts/amcharts5/xy";
import { forIn, map, pick } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class extends BaseChart {
  tooltipValuesField = "tooltipValues";

  initChart() {
    this.root.container.children.clear();
    this.createChart();

    const series = [this.settings.displayMetric, this.settings.displayMetricSecondary];
    series.forEach((name, index) => {
      this.createSeries(name, index);
    });

    this.showLegend(
      map(this.data, this.settings.decompositionMetric) as string[],
      (this.chart as XYChart).series.values[0].dataItems as LegendData,
      false,
      false
    );
  }

  protected createChart() {
    const holeSize = Number(this.settings.holeSize);
    const innerRadius = Number.isNaN(holeSize) ? this.settings.holeSize.default : holeSize;

    const chartSettings: IPieChartSettings = {
      radius: percent(70),
      y: percent(0),
      layout: this.layout,
    };

    if (innerRadius !== 0) {
      chartSettings.innerRadius = percent(innerRadius);
    }

    this.chart = this.root.container.children.push(PieChart.new(this.root, chartSettings));
  }

  protected createSeries(name: string, index: number) {
    const series = (this.chart as PieChart).series.push(
      PieSeries.new(this.root, {
        name,
        valueField: name,
        categoryField: this.settings.decompositionMetric,
      })
    );

    this.createTooltip(series as TooltipSeries, (target) => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext;

      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (key === name || key === this.settings.decompositionMetric || key === this.tooltipValuesField) {
            param.push({ key, value });
          }
        });
      }
      return param;
    });

    series.get("colors")?.set("colors", this.palette);

    const scaleValue = 0.9 + index / 10;
    series.slices.template.setAll({
      scale: scaleValue,
    });

    const hoverScaleDiff = 0.02;
    series.slices.template.states.create("hover", { scale: scaleValue + hoverScaleDiff });

    series.labels.template.setAll({
      visible: false,
      forceHidden: true,
    });
    series.ticks.template.setAll({
      visible: false,
      forceHidden: true,
    });

    series.data.setAll(this.data);
  }

  protected mapData(data: ChartDataRecord[]) {
    if (!data || !Object.keys(data)) {
      return [];
    }

    data = data.map((item) =>
      pick(item, [this.settings.decompositionMetric, this.settings.displayMetric, this.settings.displayMetricSecondary])
    );

    if (!this.settings.decompositionMetric || !this.settings.displayMetric || !this.settings.displayMetricSecondary) {
      return [];
    }

    data.sort((a, b) => (b[this.settings.displayMetric] as number) - (a[this.settings.displayMetric] as number));

    let resultData: ChartDataRecord[] = data;
    resultData = this.showItems(resultData, this.settings.decompositionMetric, this.settings.displayMetric);
    return resultData.map((item) => ({
      ...item,
      tooltipValues: {
        [this.settings.decompositionMetric]: this.formatters[this.settings.decompositionMetric](
          item[this.settings.decompositionMetric]
        ),
        [this.settings.displayMetric]: this.formatters[this.settings.displayMetric](item[this.settings.displayMetric]),
        [this.settings.displayMetricSecondary]: this.formatters[this.settings.displayMetricSecondary](
          item[this.settings.displayMetricSecondary]
        ),
      },
    }));
  }
}
